import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { StatusBadge } from "../components/StatusBadge";
import { useAuth } from "../context/AuthContext";

const SOCIALS = [["website", "Website"], ["instagram", "Instagram"], ["x", "X / Twitter"], ["facebook", "Facebook"], ["tiktok", "TikTok"], ["youtube", "YouTube"], ["goodreads", "Goodreads"]];

function Profile() {
  const [p, setP] = useState(null);
  const [v, setV] = useState(0);
  const ref = useRef();
  useEffect(() => { api.get("/showcase/profile").then((r) => setP({ ...r.data, socials: r.data.socials || {} })).catch((e) => toast.error(errMsg(e))); }, []);
  if (!p) return null;
  const save = async () => { try { await api.put("/showcase/profile", p); toast.success("Author profile saved"); } catch (e) { toast.error(errMsg(e)); } };
  const photo = async (f) => { if (!f) return; const fd = new FormData(); fd.append("file", f); try { await api.post("/showcase/profile/photo", fd); setP({ ...p, photo_path: "x" }); setV(v + 1); toast.success("Photo updated"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="panel p-6 space-y-4" data-testid="showcase-profile-form">
      <p className="eyebrow">About the author</p>
      <div className="flex gap-5 items-center">
        <div className="w-24 h-24 rounded-full overflow-hidden emblem-ring bg-black/40 shrink-0">{p.photo_path && <img src={`${API}/public/author/photo?v=${v}`} alt="Author" className="w-full h-full object-cover" data-testid="profile-photo" />}</div>
        <input ref={ref} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => photo(e.target.files[0])} data-testid="profile-photo-input" />
        <Button variant="outline" size="sm" className="border-slate-700" onClick={() => ref.current.click()} data-testid="profile-photo-button"><Upload size={13} className="mr-1" />{p.photo_path ? "Change photo" : "Add photo"}</Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs text-slate-400">Name shown</Label><Input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} data-testid="profile-name-input" /></div>
        <div className="space-y-1"><Label className="text-xs text-slate-400">Headline</Label><Input value={p.headline} placeholder="e.g. Author of dark fantasy" onChange={(e) => setP({ ...p, headline: e.target.value })} data-testid="profile-headline-input" /></div>
      </div>
      <Textarea value={p.bio} placeholder="Your bio" onChange={(e) => setP({ ...p, bio: e.target.value })} className="min-h-[120px]" data-testid="profile-bio-input" />
      <div className="grid sm:grid-cols-2 gap-2">{SOCIALS.map(([k, l]) => <Input key={k} placeholder={`${l} (https://…)`} value={p.socials[k] || ""} onChange={(e) => setP({ ...p, socials: { ...p.socials, [k]: e.target.value } })} className="h-8 text-sm" data-testid={`profile-social-${k}`} />)}</div>
      <Button onClick={save} className="bg-amber-600 hover:bg-amber-500 glow-btn" data-testid="profile-save-button">Save profile</Button>
    </div>
  );
}

export default function ShowcaseProfile() {
  const { user } = useAuth();
  const [books, setBooks] = useState([]);
  useEffect(() => { if (user?.role === "owner") api.get("/books").then((r) => setBooks(r.data)); }, [user]);
  if (user?.role !== "owner") return <div className="p-8"><div className="panel p-6 text-sm text-slate-400" data-testid="showcase-owner-only">Only the Master Owner can manage the public showcase.</div></div>;
  return (
    <div className="p-4 sm:p-8 max-w-5xl space-y-6" data-testid="showcase-admin-page">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div><p className="eyebrow mb-2">Public page</p><h1 className="font-display text-4xl sm:text-5xl">Author Showcase</h1>
          <p className="text-sm text-slate-400 mt-3 max-w-xl">Readers see only the books you switch on, with their cover, store links, details and a sample from the approved version.</p></div>
        <a href="/authors" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-amber-200 hover:text-white" data-testid="view-public-showcase">View public page <ExternalLink size={13} /></a>
      </div>
      <Profile />
      <div className="panel divide-y divide-slate-800" data-testid="showcase-book-list">
        <p className="eyebrow p-4">Books</p>
        {books.map((b) => (
          <div key={b.id} className="p-4 flex justify-between items-center gap-3" data-testid={`showcase-row-${b.id}`}>
            <span className="text-sm truncate">{b.title}</span>
            <div className="flex gap-3 items-center shrink-0"><StatusBadge status={b.showcase?.enabled ? "PASS" : "not_started"} label={b.showcase?.enabled ? "on showcase" : "hidden"} />
              <Link to={`/app/books/${b.id}?tab=showcase`} className="text-xs text-amber-300 hover:underline" data-testid={`showcase-edit-${b.id}`}>Edit</Link></div>
          </div>
        ))}
      </div>
    </div>
  );
}
